import React, { useEffect, useRef, useState } from 'react';
import { Button, Row, Tag, Tabs, Select, message, Modal } from 'antd';
import { PageContentLayout } from '../../../../shared/layout';
import {
  DeleteOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import { withRouter, useParams } from 'react-router-dom';
import Text from 'antd/lib/typography/Text';
import { useProjectContext } from '../../../../../core/components/projects/useProjectContext';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import AppOverlaySpinner from '../shared/AppOverlaySpinner';
import moment from 'moment';
import ProjectSettings from './ProjectSettings';
import { AddExistingTaskModal } from '../modals';
import ProjectMilestone from './ProjectMilestone';
import ProjectGantt from './ProjectGantt';
import ProjectDetailsHeader from './ProjectDetailsHeader';
import ProjectDetailsMobileHeader from './ProjectDetailsMobileHeader';
import ProjectDetailsStats from './ProjectDetailsStats';
import ProjectDetailsMobileStats from './ProjectDetailsMobileStats';
import ProjectTasksList from './ProjectTasksList';
import useResponsiveTask from '../../hooks/useResponsiveTask';
import { space, bg, link, border, radius, fontSize } from '../../configs/designTokens';
import '../../styles/index.scss';
const { TabPane } = Tabs;
const { Option } = Select;

/**
 * Decides which view the project details page should show, given the requested id and the
 * (provider-global, persistent) project query state. Pure so the state machine can be unit
 * tested without rendering — see ProjectDetails.test.js.
 *
 * @returns {'no-selection'|'loading'|'missing'|'content'}
 *  - 'loading': no matching project yet and the fetch hasn't settled empty (cold mount,
 *    in-flight, or a stale result still held from a previously viewed project).
 *  - 'missing': the fetch for THIS id finished with no record (deleted/invalid id, no
 *    access, or an error). `requestedProjectId === currentProjectId` ensures the empty
 *    result belongs to the current id and not a previous one.
 *  - 'content': the matching project AND the current user are both in hand. currentUser is
 *    required because the access check (hasAccess) needs it — until CURRENT_USER resolves,
 *    hasAccess() returns false, which would flash "Access Denied" on refresh whenever the
 *    project query wins the race against the user query.
 */
export const getProjectDetailsView = ({
  currentProjectId,
  currentProject,
  projectLoading,
  requestedProjectId,
  currentUser,
}) => {
  if (!currentProjectId) return 'no-selection';
  if (currentProject && currentProject.id === currentProjectId && currentUser) return 'content';
  if (!projectLoading && !currentProject && requestedProjectId === currentProjectId) return 'missing';
  return 'loading';
};

const ProjectDetails = ({ history }) => {
  const { id: currentProjectId } = useParams();
  const [isSettingsModalVisible, setIsSettingsModalVisible] = useState(false);
  const [isAddTaskModalVisible, setIsAddTaskModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [isAddToMilestoneModalVisible, setIsAddToMilestoneModalVisible] = useState(false);
  const [selectedMilestoneId, setSelectedMilestoneId] = useState(null);
  const [isAddingToMilestone, setIsAddingToMilestone] = useState(false);
  const { isMobile, isTablet } = useResponsiveTask();

  const {
    currentUser,
    fetchProject,
    currentProject,
    projectLoading,
    projectError,
    refetchProject,
    updateProject,
    updateProjectLoading,
    fetchMilestones,
    milestones,
    updateMilestone,
  } = useProjectContext();
  const { updateTask } = useTaskContext();

  // Tracks the id of the most recent project we requested, so we can tell a "settled
  // empty" result for the CURRENT project apart from a stale result the persistent
  // provider still holds from a previously viewed project.
  const requestedProjectIdRef = useRef(null);

  // Whether we reached this page via an in-app navigation (PUSH/REPLACE) vs. a fresh load /
  // refresh / direct link (POP). On a fresh load there is no in-app entry to return to, so
  // history.goBack() is a no-op on the first click — fall back to the projects board instead.
  const arrivedInAppRef = useRef(history.action !== 'POP');

  const handleBack = () => {
    if (arrivedInAppRef.current) {
      history.goBack();
    } else {
      history.push('/tasks/projects');
    }
  };

  useEffect(() => {
    if (currentProjectId) {
      requestedProjectIdRef.current = currentProjectId;
      fetchProject({
        variables: { where: { id: currentProjectId } },
      });
      fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProjectId]);

  document.title =
    currentProject && currentProject.name ? `Project: ${currentProject.name}` : 'Project Details';

  const handleStatusUpdate = async (selectedStatus) => {
    try {
      await updateProject({
        variables: {
          data: {
            status: selectedStatus,
          },
          where: { id: currentProjectId },
        },
      });
      await refetchProject();
      message.success('Project status updated successfully');
    } catch (error) {
      console.error('Failed to update project status:', error);
      message.error('Failed to update project status');
    }
  };

  const handleSettingsSave = async (values) => {
    try {
      const { team, owner, ...projectFields } = values;

      const updateData = { ...projectFields };

      if (team !== undefined) {
        const currentTeamIds = currentProject?.team?.map(member => member.id) || [];
        const newTeamIds = team || [];

        const usersToDisconnect = currentTeamIds.filter(id => !newTeamIds.includes(id));
        const usersToConnect = newTeamIds.filter(id => !currentTeamIds.includes(id));

        const teamUpdate = {};
        if (usersToDisconnect.length > 0) {
          teamUpdate.disconnect = usersToDisconnect.map(id => ({ id }));
        }
        if (usersToConnect.length > 0) {
          teamUpdate.connect = usersToConnect.map(id => ({ id }));
        }

        if (Object.keys(teamUpdate).length > 0) {
          updateData.team = teamUpdate;
        }
      }

      if (owner !== undefined) {
        const currentOwnerId = currentProject?.owner?.id;

        if (owner === null || owner === '') {
          if (currentOwnerId) {
            updateData.owner = { disconnect: true };
          }
        } else if (owner !== currentOwnerId) {
          updateData.owner = { connect: { id: owner } };
        }
      }

      await updateProject({
        variables: {
          data: updateData,
          where: { id: currentProjectId },
        },
      });

      await refetchProject();
      return Promise.resolve();
    } catch (error) {
      console.error('Update failed:', error);
      return Promise.reject(error);
    }
  };

  const handleRemoveTask = async (taskId, taskName) => {
    Modal.confirm({
      title: 'Remove Task from Project',
      content: `Are you sure you want to remove "${taskName}" from this project? This will also remove the task from any milestones in this project.`,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
            },
          });

          const milestonesWithTask = milestones.filter(milestone =>
            milestone.tasks?.some(task => task.id === taskId)
          );

          for (const milestone of milestonesWithTask) {
            await updateMilestone({
              variables: {
                data: {
                  tasks: {
                    disconnect: [{ id: taskId }],
                  },
                },
                where: { id: milestone.id },
              },
            });
          }

          await updateProject({
            variables: {
              data: {
                tasks: {
                  disconnect: [{ id: taskId }],
                },
              },
              where: { id: currentProjectId },
            },
          });

          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
              orderBy: 'dueDate_ASC',
            },
          });

          await refetchProject();

          const milestoneMessage = milestonesWithTask.length > 0
            ? ` and removed from ${milestonesWithTask.length} milestone(s)`
            : '';
          message.success(`Task removed from project successfully${milestoneMessage}`);
        } catch (error) {
          console.error('Failed to remove task:', error);
          message.error('Failed to remove task from project');
        }
      },
    });
  };

  const handleBulkRemoveTasks = async () => {
    const taskCount = selectedTaskIds.length;
    const taskNames = currentProject?.tasks
      ?.filter(task => selectedTaskIds.includes(task.id))
      .map(task => task.name)
      .slice(0, 3);
    const displayNames = taskNames?.join(', ') + (taskCount > 3 ? `, and ${taskCount - 3} more` : '');

    Modal.confirm({
      title: `Remove ${taskCount} Task${taskCount > 1 ? 's' : ''} from Project`,
      content: `Are you sure you want to remove ${taskCount > 1 ? 'these tasks' : 'this task'} from this project? ${taskCount <= 3 ? `(${displayNames})` : `Including: ${displayNames}`}. This will also remove them from any milestones in this project.`,
      okText: 'Remove All',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
            },
          });

          const affectedMilestones = milestones.filter(milestone =>
            milestone.tasks?.some(task => selectedTaskIds.includes(task.id))
          );

          for (const milestone of affectedMilestones) {
            const tasksToDisconnect = milestone.tasks
              .filter(task => selectedTaskIds.includes(task.id))
              .map(task => ({ id: task.id }));

            if (tasksToDisconnect.length > 0) {
              await updateMilestone({
                variables: {
                  data: {
                    tasks: {
                      disconnect: tasksToDisconnect,
                    },
                  },
                  where: { id: milestone.id },
                },
              });
            }
          }

          await updateProject({
            variables: {
              data: {
                tasks: {
                  disconnect: selectedTaskIds.map(id => ({ id })),
                },
              },
              where: { id: currentProjectId },
            },
          });

          await fetchMilestones({
            variables: {
              where: { project: { id: currentProjectId } },
              orderBy: 'dueDate_ASC',
            },
          });

          await refetchProject();
          setSelectedTaskIds([]);

          const milestoneMessage = affectedMilestones.length > 0
            ? ` and removed from ${affectedMilestones.length} milestone(s)`
            : '';
          message.success(`${taskCount} task${taskCount > 1 ? 's' : ''} removed from project successfully${milestoneMessage}`);
        } catch (error) {
          console.error('Failed to remove tasks:', error);
          message.error('Failed to remove tasks from project');
        }
      },
    });
  };

  const handleAddToMilestone = () => {
    setIsAddToMilestoneModalVisible(true);
  };

  const handleAddToMilestoneConfirm = async () => {
    if (!selectedMilestoneId || selectedTaskIds.length === 0) {
      message.warning('Please select a milestone');
      return;
    }

    setIsAddingToMilestone(true);

    try {
      const targetMilestone = milestones.find(m => m.id === selectedMilestoneId);
      const currentMilestoneTaskIds = targetMilestone?.tasks?.map(t => t.id) || [];
      const tasksToConnect = selectedTaskIds.filter(id => !currentMilestoneTaskIds.includes(id));

      if (tasksToConnect.length === 0) {
        message.info('Selected tasks are already in this milestone');
        setIsAddToMilestoneModalVisible(false);
        setIsAddingToMilestone(false);
        return;
      }

      // Find tasks that are in other milestones and need to be removed first
      const tasksToReassign = [];
      for (const taskId of tasksToConnect) {
        const existingMilestone = milestones.find(m =>
          m.id !== selectedMilestoneId && m.tasks?.some(t => t.id === taskId)
        );
        if (existingMilestone) {
          tasksToReassign.push({
            taskId,
            fromMilestone: existingMilestone,
          });
        }
      }

      // Remove tasks from their current milestones first
      for (const { taskId, fromMilestone } of tasksToReassign) {
        await updateMilestone({
          variables: {
            data: {
              tasks: {
                disconnect: [{ id: taskId }],
              },
            },
            where: { id: fromMilestone.id },
          },
        });
      }

      // Add tasks to the selected milestone
      await updateMilestone({
        variables: {
          data: {
            tasks: {
              connect: tasksToConnect.map(id => ({ id })),
            },
          },
          where: { id: selectedMilestoneId },
        },
      });

      await fetchMilestones({
        variables: {
          where: { project: { id: currentProjectId } },
          orderBy: 'dueDate_ASC',
        },
      });

      await refetchProject();

      let successMessage = `Successfully added ${tasksToConnect.length} task(s) to milestone`;
      if (tasksToReassign.length > 0) {
        successMessage += ` (${tasksToReassign.length} reassigned from other milestones)`;
      }
      message.success(successMessage);
      setIsAddToMilestoneModalVisible(false);
      setSelectedMilestoneId(null);
      setSelectedTaskIds([]);
    } catch (error) {
      console.error('Failed to add tasks to milestone:', error);
      message.error('Failed to add tasks to milestone');
    } finally {
      setIsAddingToMilestone(false);
    }
  };

  const view = getProjectDetailsView({
    currentProjectId,
    currentProject,
    projectLoading,
    requestedProjectId: requestedProjectIdRef.current,
    currentUser,
  });

  if (view === 'no-selection') return <p>Select a project to see the details.</p>;

  // Not-yet-started / in-flight / stale fetch — spinner instead of flashing. A
  // cache-and-network refetch keeps currentProject, so a matching project renders straight
  // to content (no flicker).
  if (view === 'loading') {
    return <AppOverlaySpinner />;
  }

  // Query settled with no matching project — render a friendly state instead of crashing
  // the main render (which dereferences currentProject), spinning forever (the original
  // bug), or flashing "not found" for one frame on load/navigation.
  if (view === 'missing') {
    return (
      <div className="project-details__container" style={{ paddingInline: '5%', height: '100vh' }}>
        <Row className="project-details__back-row" style={{ paddingBottom: space[5] }}>
          <Button
            className="project-details__back-btn"
            onClick={handleBack}
          >
            {<LeftOutlined style={{ paddingRight: space[2.5] }} />} <Text strong> Back </Text>
          </Button>
        </Row>
        <div className="project-details__access-denied">
          <div style={{ textAlign: 'center' }}>
            <Text className="project-details__access-title">
              {projectError ? 'Something went wrong' : 'Project not found'}
            </Text>
            <Text className="project-details__access-message">
              {projectError
                ? 'We could not load this project. Please try again.'
                : 'This project may have been removed, or you do not have permission to view it.'}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  const hasAccess = () => {
    if (!currentProject || !currentUser) return false;

    // Channel partners don't have access to project management
    const userOrgLevel = currentUser.role?.org?.level;
    if (userOrgLevel === 'CHANNEL_PARTNER') return false;

    const currentUserOrgId = currentUser.role?.org?.id;
    if (!currentUserOrgId) return false;

    // Check if project owner has any role in an org that matches the current user's access
    // This matches the logic from ProjectBoard filtering
    const ownerRoles = currentProject.owner?.roles || [];

    return ownerRoles.some(role => {
      const roleOrgId = role.org?.id;
      const roleOrgParentId = role.org?.parentOrg?.id;

      // Same org
      if (roleOrgId === currentUserOrgId) return true;

      // Current user's org is parent of this role's org
      if (roleOrgParentId === currentUserOrgId) return true;

      return false;
    });
  };

  if (currentProject && !hasAccess()) {
    return (
      <div className="project-details__container" style={{ paddingInline: '5%', height: '100vh' }}>
        <Row className="project-details__back-row" style={{ paddingBottom: space[5] }}>
          <Button
            className="project-details__back-btn"
            onClick={handleBack}
          >
            {<LeftOutlined style={{ paddingRight: space[2.5] }} />}{' '}
            <Text strong> Back </Text>
          </Button>
        </Row>
        <div className="project-details__access-denied">
          <div style={{ textAlign: 'center' }}>
            <Text className="project-details__access-title">
              Access Denied
            </Text>
            <Text className="project-details__access-message">
              You do not have permission to view this project.
            </Text>
            <Text className="project-details__access-org">
              This project belongs to: <strong>{currentProject.owner?.roles?.[0]?.org?.name || 'Unknown'} organization</strong>
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-details__container" style={{ paddingInline: isMobile ? 0 : '5%' }}>
      {/* Mobile: Compact Header */}
      {isMobile ? (
        <ProjectDetailsMobileHeader
          project={currentProject}
          onBack={handleBack}
          onStatusUpdate={handleStatusUpdate}
          onSettingsClick={() => setIsSettingsModalVisible(true)}
          updateLoading={updateProjectLoading || projectLoading}
        />
      ) : (
        <ProjectDetailsHeader
          project={currentProject}
          onBack={handleBack}
          onStatusUpdate={handleStatusUpdate}
          onSettingsClick={() => setIsSettingsModalVisible(true)}
          updateLoading={updateProjectLoading || projectLoading}
          isMobile={isMobile}
          isTablet={isTablet}
        />
      )}

      <ProjectSettings
        visible={isSettingsModalVisible}
        onCancel={() => setIsSettingsModalVisible(false)}
        project={currentProject}
        currentUser={currentUser}
        onSave={handleSettingsSave}
      />

      <AddExistingTaskModal
        visible={isAddTaskModalVisible}
        onCancel={() => setIsAddTaskModalVisible(false)}
        currentUser={currentUser}
        currentProject={currentProject}
        updateProject={updateProject}
        refetchProject={refetchProject}
        updateProjectLoading={updateProjectLoading}
        onAddTasks={async (taskIds, selectedTasks, selectedMilestoneIds = []) => {
          try {
            const currentTaskIds = currentProject?.tasks?.map(task => task.id) || [];
            const tasksToConnect = taskIds.filter(id => !currentTaskIds.includes(id));

            if (tasksToConnect.length > 0) {
              const projectMemberIds = currentProject?.team?.map(member => member.id) || [];
              const projectOwnerId = currentProject?.owner?.id;
              const allProjectUserIds = [...projectMemberIds, projectOwnerId].filter(Boolean);

              let convertedCount = 0;

              for (const task of selectedTasks) {
                if (task.assigneeUsers && task.assigneeUsers.length > 0) {
                  const assigneeIds = task.assigneeUsers.map(u => u.id);
                  const nonMemberAssigneeIds = assigneeIds.filter(id => !allProjectUserIds.includes(id));

                  if (nonMemberAssigneeIds.length > 0) {
                    const currentCollaboratorIds = task.collaborators?.map(c => c.id) || [];
                    const usersToAddAsCollaborators = nonMemberAssigneeIds.filter(id => !currentCollaboratorIds.includes(id));
                    const updateData = {};

                    if (nonMemberAssigneeIds.length > 0) {
                      updateData.assigneeUsers = {
                        disconnect: nonMemberAssigneeIds.map(id => ({ id }))
                      };
                    }

                    if (usersToAddAsCollaborators.length > 0) {
                      updateData.collaborators = {
                        connect: usersToAddAsCollaborators.map(id => ({ id }))
                      };
                    }

                    await updateTask({
                      variables: {
                        data: updateData,
                        where: { id: task.id },
                      },
                    });

                    convertedCount += nonMemberAssigneeIds.length;
                  }
                }
              }

              await updateProject({
                variables: {
                  data: {
                    tasks: {
                      connect: tasksToConnect.map(id => ({ id })),
                    },
                  },
                  where: { id: currentProjectId },
                },
              });

              if (selectedMilestoneIds.length > 0) {
                for (const milestoneId of selectedMilestoneIds) {
                  await updateMilestone({
                    variables: {
                      data: {
                        tasks: {
                          connect: tasksToConnect.map(id => ({ id })),
                        },
                      },
                      where: { id: milestoneId },
                    },
                  });
                }
              }

              await fetchMilestones({
                variables: {
                  where: { project: { id: currentProjectId } },
                  orderBy: 'dueDate_ASC',
                },
              });

              await refetchProject();

              let successMessage = `Successfully added ${tasksToConnect.length} task(s) to the project`;
              if (selectedMilestoneIds.length > 0) {
                successMessage += ` and ${selectedMilestoneIds.length} milestone(s)`;
              }
              if (convertedCount > 0) {
                successMessage += `. ${convertedCount} assignee(s) converted to collaborators`;
              }
              message.success(successMessage);
            } else {
              message.info('No new tasks to add');
            }
          } catch (error) {
            console.error('Failed to add tasks to project:', error);
            message.error('Failed to add tasks to the project. Please try again.');
            throw error;
          }
        }}
      />

      {/* Add to Milestone Modal */}
      <Modal
        title="Add Tasks to Milestone"
        open={isAddToMilestoneModalVisible}
        onCancel={() => {
          if (!isAddingToMilestone) {
            setIsAddToMilestoneModalVisible(false);
            setSelectedMilestoneId(null);
          }
        }}
        onOk={handleAddToMilestoneConfirm}
        okText="Add to Milestone"
        confirmLoading={isAddingToMilestone}
        okButtonProps={{ disabled: !selectedMilestoneId || isAddingToMilestone }}
        cancelButtonProps={{ disabled: isAddingToMilestone }}
        closable={!isAddingToMilestone}
        maskClosable={!isAddingToMilestone}
        width={600}
      >
        <div>
          <Text className="add-milestone-modal__description">
            Select a milestone to add the {selectedTaskIds.length} selected task{selectedTaskIds.length > 1 ? 's' : ''} to:
          </Text>

          {/* Warning for tasks that will be reassigned */}
          {selectedMilestoneId && (() => {
            const tasksWithMilestones = selectedTaskIds.filter(taskId => {
              return milestones.some(m =>
                m.id !== selectedMilestoneId && m.tasks?.some(t => t.id === taskId)
              );
            });

            if (tasksWithMilestones.length > 0) {
              const affectedMilestones = new Set();
              tasksWithMilestones.forEach(taskId => {
                const m = milestones.find(m =>
                  m.id !== selectedMilestoneId && m.tasks?.some(t => t.id === taskId)
                );
                if (m) affectedMilestones.add(m.name);
              });

              return (
                <div className="add-milestone-modal__warning">
                  <ExclamationCircleOutlined className="add-milestone-modal__warning-icon" />
                  <div className="add-milestone-modal__warning-content">
                    <Text strong className="add-milestone-modal__warning-title">
                      Warning: Task Reassignment
                    </Text>
                    <Text className="add-milestone-modal__warning-text">
                      {tasksWithMilestones.length} of the selected task{tasksWithMilestones.length > 1 ? 's are' : ' is'} currently assigned to {affectedMilestones.size > 1 ? 'other milestones' : 'another milestone'} ({Array.from(affectedMilestones).join(', ')}). {tasksWithMilestones.length > 1 ? 'They' : 'It'} will be moved to the selected milestone.
                    </Text>
                  </div>
                </div>
              );
            }
            return null;
          })()}

          <Select
            placeholder="Select a milestone"
            style={{ width: '100%' }}
            value={selectedMilestoneId}
            onChange={setSelectedMilestoneId}
            size="large"
          >
            {(milestones || []).map((milestone) => (
              <Option key={milestone.id} value={milestone.id}>
                <div className="add-milestone-modal__option">
                  <div>
                    <Text strong>{milestone.name}</Text>
                    {milestone.dueDate && (
                      <Text className="add-milestone-modal__option-date">
                        Due: {moment(milestone.dueDate).format('MMM D, YYYY')}
                      </Text>
                    )}
                  </div>
                  <Tag color={milestone.status === 'COMPLETED' ? 'success' : milestone.status === 'IN_PROGRESS' ? 'processing' : 'default'}>
                    {milestone.status?.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') || 'N/A'}
                  </Tag>
                </div>
              </Option>
            ))}
          </Select>
          {(milestones || []).length === 0 && (
            <div className="add-milestone-modal__empty">
              <Text className="add-milestone-modal__empty-text">
                No milestones available. Please create a milestone first.
              </Text>
            </div>
          )}
        </div>
      </Modal>

      {/* Mobile: Collapsible Stats */}
      {isMobile && (
        <ProjectDetailsMobileStats project={currentProject} />
      )}

      <PageContentLayout
        style={{
          backgroundColor: bg.card,
          width: '100%',
          paddingTop: isMobile ? '0' : '2vh',
          paddingInline: isMobile ? '4%' : 0,
        }}
      >
        {/* Desktop: Full Stats */}
        {!isMobile && (
          <ProjectDetailsStats project={currentProject} isMobile={false} />
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: isMobile || isTablet ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: isMobile || isTablet ? 'flex-start' : 'center',
            marginBottom: space[4],
            gap: isMobile || isTablet ? space[4] : 0,
          }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            type="card"
            className="task-tabs-details-history"
            tabBarStyle={{
              border: `4px solid ${border.slateLight}`,
              borderRadius: radius.md,
              width: isMobile ? '100%' : 'fit-content',
              display: 'inline-flex',
              gap: space[0],
              margin: space[0],
            }}
          >
            <TabPane tab="Tasks" key="1" />
            <TabPane tab="Milestones" key="2" />
            <TabPane tab="Gantt Chart" key="3" />
          </Tabs>

          {activeTab === '1' && !isMobile && (
            <div
              style={{
                display: 'flex',
                flexDirection: isTablet ? 'column' : 'row',
                gap: space[2],
                alignItems: 'center',
                width: isTablet ? '100%' : 'auto',
              }}
            >
              <Button
                style={{
                  fontSize: fontSize.button,
                  height: space[10],
                  borderRadius: radius.sm,
                  width: isTablet ? '100%' : 'auto',
                }}
                onClick={() => setIsAddTaskModalVisible(true)}
              >
                Add Existing Task
              </Button>
              <Button
                type="primary"
                style={{
                  fontSize: fontSize.button,
                  height: space[10],
                  borderRadius: radius.sm,
                  backgroundColor: link.blue,
                  width: isTablet ? '100%' : 'auto',
                }}
                onClick={() => {
                  history.push(`/tasks/form/new?projectId=${currentProjectId}`);
                }}
              >
                Create Task
              </Button>
            </div>
          )}

          {/* Mobile: Add Existing Task action */}
          {activeTab === '1' && isMobile && (
            <Button
              block
              icon={<PlusOutlined />}
              onClick={() => setIsAddTaskModalVisible(true)}
              style={{
                height: space[11],
                borderRadius: radius.md,
                fontSize: fontSize.button,
                marginTop: space[2],
              }}
            >
              Add Existing Task
            </Button>
          )}
        </div>

        {activeTab === '1' && selectedTaskIds.length > 0 && (
          <div
            className="project-details__bulk-bar"
            style={{
              flexDirection: isMobile || isTablet ? 'column' : 'row',
              alignItems: isMobile || isTablet ? 'flex-start' : 'center',
              gap: isMobile || isTablet ? space[3] : 0,
            }}
          >
            <div className="project-details__bulk-info">
              <Text className="project-details__bulk-count">
                {selectedTaskIds.length} task{selectedTaskIds.length > 1 ? 's' : ''} selected
              </Text>
              <Button
                size="small"
                type="text"
                onClick={() => setSelectedTaskIds([])}
                className="project-details__bulk-clear"
              >
                Clear selection
              </Button>
            </div>
            <div
              className="project-details__bulk-actions"
              style={{
                flexDirection: isMobile ? 'column' : 'row',
                width: isMobile || isTablet ? '100%' : 'auto',
              }}
            >
              <Button
                icon={<PlusOutlined />}
                onClick={handleAddToMilestone}
                disabled={!milestones || milestones.length === 0 || isAddingToMilestone}
                loading={isAddingToMilestone}
                className="project-details__bulk-btn"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Add to Milestone
              </Button>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBulkRemoveTasks}
                disabled={isAddingToMilestone}
                className="project-details__bulk-btn"
                style={{ width: isMobile ? '100%' : 'auto' }}
              >
                Remove from Project
              </Button>
            </div>
          </div>
        )}

        {activeTab === '1' && (
          <ProjectTasksList
            tasks={currentProject?.tasks || []}
            milestones={milestones}
            selectedTaskIds={selectedTaskIds}
            onSelectionChange={setSelectedTaskIds}
            onRemoveTask={handleRemoveTask}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        )}
        {activeTab === '2' && (
          <ProjectMilestone currentProjectId={currentProjectId} />
        )}
        {activeTab === '3' && (
          <ProjectGantt currentProjectId={currentProjectId} />
        )}
      </PageContentLayout>

      {/* Mobile FAB for creating tasks */}
      {isMobile && activeTab === '1' && (
        <Button
          type="primary"
          shape="circle"
          icon={<PlusOutlined />}
          className="project-details__fab"
          onClick={() => history.push(`/tasks/form/new?projectId=${currentProjectId}`)}
        />
      )}
    </div>
  );
};

export default withRouter(ProjectDetails);
